/**
 * Sellers API client
 * Provides functions for seller/company profile management
 */
import apiClient from './client';
import { SellerProfile, SellerCreateData, InvoiceSettings, InvoiceSettingsUpdateData } from '../types';

// ==================== Seller Profiles ====================

/**
 * Get all seller profiles for the current user
 */
export const getSellers = async (activeOnly: boolean = true): Promise<SellerProfile[]> => {
  const response = await apiClient.get('/api/sellers/', {
    params: { active_only: activeOnly }
  });
  return response.data;
};

/**
 * Get the default seller profile
 */
export const getDefaultSeller = async (): Promise<SellerProfile | null> => {
  try {
    const response = await apiClient.get('/api/sellers/default');
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

/**
 * Get a specific seller profile by ID
 */
export const getSeller = async (sellerId: number): Promise<SellerProfile> => {
  const response = await apiClient.get(`/api/sellers/${sellerId}`);
  return response.data;
};

/**
 * Create a new seller profile
 */
export const createSeller = async (data: SellerCreateData): Promise<SellerProfile> => {
  const response = await apiClient.post('/api/sellers/', data);
  return response.data;
};

/**
 * Update an existing seller profile
 */
export const updateSeller = async (sellerId: number, data: Partial<SellerCreateData>): Promise<SellerProfile> => {
  const response = await apiClient.put(`/api/sellers/${sellerId}`, data);
  return response.data;
};

/**
 * Delete a seller profile (soft delete by default)
 */
export const deleteSeller = async (sellerId: number, permanent: boolean = false): Promise<void> => {
  await apiClient.delete(`/api/sellers/${sellerId}`, {
    params: { permanent }
  });
};

/**
 * Set a seller profile as the default
 */
export const setDefaultSeller = async (sellerId: number): Promise<SellerProfile> => {
  const response = await apiClient.post(`/api/sellers/${sellerId}/set-default`);
  return response.data;
};

// ==================== Invoice Settings ====================

/**
 * Get invoice settings for the current user
 */
export const getInvoiceSettings = async (): Promise<InvoiceSettings> => {
  const response = await apiClient.get('/api/invoice-settings/');
  return response.data;
};

/**
 * Update invoice settings
 */
export const updateInvoiceSettings = async (data: InvoiceSettingsUpdateData): Promise<InvoiceSettings> => {
  const response = await apiClient.put('/api/invoice-settings/', data);
  return response.data;
};

/**
 * Preview the next invoice number (doesn't increment)
 */
export const previewNextInvoiceNumber = async (): Promise<{
  next_number: string;
  prefix: string;
  padding: number;
  sequence: number;
}> => {
  const response = await apiClient.get('/api/invoice-settings/next-number');
  return response.data;
};

/**
 * Reset the invoice number sequence
 */
export const resetInvoiceSequence = async (startFrom: number = 1): Promise<{
  status: string;
  next_invoice_number: number;
}> => {
  const response = await apiClient.post('/api/invoice-settings/reset-sequence', null, {
    params: { start_from: startFrom }
  });
  return response.data;
};

// ==================== Helper Functions ====================

/**
 * Check if user has any seller profiles
 */
export const hasSellerProfiles = async (): Promise<boolean> => {
  const sellers = await getSellers();
  return sellers.length > 0;
};

/**
 * Get seller display name (for dropdowns)
 */
export const getSellerDisplayName = (seller: SellerProfile): string => {
  if (seller.tax_id) {
    return `${seller.name} (${seller.tax_id})`;
  }
  return seller.name;
};

/**
 * Format seller address as single line
 */
export const formatSellerAddress = (seller: SellerProfile): string => {
  const parts: string[] = [];

  if (seller.street) {
    let streetLine = seller.street;
    if (seller.street_number) {
      streetLine += ` ${seller.street_number}`;
    }
    parts.push(streetLine);
  }

  if (seller.city) {
    parts.push(seller.city);
  }

  if (seller.country) {
    parts.push(seller.country);
  }

  return parts.join(', ');
};

/**
 * Upload logo for a seller profile
 */
export const uploadSellerLogo = async (sellerId: number, file: File): Promise<SellerProfile> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiClient.post(`/api/sellers/${sellerId}/logo`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

/**
 * Delete logo for a seller profile
 */
export const deleteSellerLogo = async (sellerId: number): Promise<SellerProfile> => {
  const response = await apiClient.delete(`/api/sellers/${sellerId}/logo`);
  return response.data;
};
